import {
  INSTITUTE_ADMIN_ROLE,
  FILE_DOWNLOAD_ROLE,
  FILE_UPLOAD_ROLE,
  NUMER_ADD_ROLE,
  NUMBER_VERIFY_ROLE,
} from "@sperrdatei/dto/roles";
import { useState, useImperativeHandle, forwardRef, useContext } from "react";

import { ApiContext } from "../../api-context";
import { Modal } from "../../components/modal";
import { ToastContext } from "../../components/toast";

import type { FormMode, ForwardFormRef } from "./shared";
import type { PROJECT_ROLE } from "@sperrdatei/dto/roles";
import type { User } from "@sperrdatei/dto/types";
import type { ForwardedRef, JSX, RefObject } from "react";

type AddUserFormProps =
  | { mode: "INSERT"; domains: string[] }
  | { mode: "EDIT"; domains: string[]; selectedUser: User };

const AddUserForm = (
  props: AddUserFormProps,
  ref: ForwardedRef<{
    validate: () => boolean;
    getResult: () => User;
    getMode: () => FormMode;
  }>,
): JSX.Element => {
  const toast = useContext(ToastContext);
  const { apiFetch } = useContext(ApiContext);

  const [validated, setValidated] = useState(false);
  const [activeDomain, setActiveDomain] = useState(props.domains[0]);
  const [isEmailDropdownVisible, setIsEmailDropdownVisible] = useState(false);
  const [isPasswordResetEnabled, setIsPasswordResetEnabled] =
    useState<boolean>(true);
  const [usernameError, setUsernameError] = useState<string | undefined>();
  const [user, setUser] = useState<User>(
    props.mode === "INSERT"
      ? { username: "", domain: "", firstName: "", lastName: "", roles: [] }
      : props.selectedUser,
  );

  useImperativeHandle(ref, () => ({
    getResult: (): User => user,
    getMode: (): FormMode => props.mode,
    setError: (error: string): void => {
      setValidated(true);
      setUsernameError(error);
    },
    validate: (): boolean => {
      setValidated(true);
      if (!user.username) {
        setUsernameError("Angabe notwendig");
        return false;
      }
      return true;
    },
  }));

  const updateRole = (active: boolean, role: PROJECT_ROLE["key"]): void => {
    if (active) {
      if (!user.roles.includes(role)) {
        setUser({
          ...user,
          roles: [...user.roles, role],
        });
      }
    } else {
      setUser({
        ...user,
        roles: user.roles.filter((r) => r !== role),
      });
    }
  };

  return (
    <>
      <div className="mb-3">
        <label htmlFor="username" className="form-text">
          Benutzername / E-Mail
        </label>
        <div className="input-group has-validation">
          <input
            type="text"
            id="username"
            value={user.username}
            disabled={props.mode !== "INSERT"}
            className={
              usernameError
                ? `form-control ${validated ? "is-invalid" : ""}`
                : `form-control ${validated ? "is-valid" : ""}`
            }
            onChange={(e) => {
              setUser({
                ...user,
                username: e.target.value,
                domain: activeDomain,
              });
            }}
            required
            autoFocus
          />
          <button
            className="btn btn-outline-secondary dropdown-toggle domain-toggle"
            type="button"
            disabled={props.mode !== "INSERT"}
            onClick={() => setIsEmailDropdownVisible(true)}
          >
            @{activeDomain}
          </button>
          <ul
            className={
              isEmailDropdownVisible
                ? "dropdown-menu dropdown-menu-end show"
                : "dropdown-menu dropdown-menu-end"
            }
          >
            {props.domains.map((domain) => (
              <li key={domain}>
                <a
                  className="dropdown-item"
                  href="#"
                  onClick={() => {
                    setUser({
                      ...user,
                      username: user.username,
                      domain: domain,
                    });
                    setActiveDomain(domain);
                    setIsEmailDropdownVisible(false);
                  }}
                >
                  {domain}
                </a>
              </li>
            ))}
          </ul>
          <div className="invalid-feedback">
            {usernameError ? usernameError : ""}
          </div>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-6">
          <label htmlFor="firstName" className="form-text">
            Vorname
          </label>
          <div>
            <input
              type="text"
              id="firstName"
              value={user.firstName}
              className={
                !user.firstName
                  ? `form-control ${validated ? "is-invalid" : ""}`
                  : `form-control ${validated ? "is-valid" : ""}`
              }
              onChange={(e) => {
                setUser({ ...user, firstName: e.target.value });
              }}
              required
            />
            <div className="invalid-feedback">Angabe notwendig</div>
          </div>
        </div>
        <div className="col-6">
          <label htmlFor="lastName" className="form-text">
            Nachname
          </label>
          <div>
            <input
              type="text"
              id="lastName"
              value={user.lastName}
              className={
                !user.lastName
                  ? `form-control ${validated ? "is-invalid" : ""}`
                  : `form-control ${validated ? "is-valid" : ""}`
              }
              onChange={(e) => {
                setUser({ ...user, lastName: e.target.value });
              }}
              required
            />
            <div className="invalid-feedback">Angabe notwendig</div>
          </div>
        </div>
      </div>
      <div className="row mb-2">
        <div className="col-12">
          <span className="form-text">Benutzerrechte</span>
        </div>
        <div className="col-6">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="institutAdminCheck"
              checked={user.roles.includes(INSTITUTE_ADMIN_ROLE.key)}
              onChange={(e) =>
                updateRole(e.target.checked, INSTITUTE_ADMIN_ROLE.key)
              }
            />
            <label
              className="form-check-label small"
              htmlFor="institutAdminCheck"
            >
              Institut Verwaltung
            </label>
          </div>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="fileDownloadCheck"
              checked={user.roles.includes(FILE_DOWNLOAD_ROLE.key)}
              onChange={(e) =>
                updateRole(e.target.checked, FILE_DOWNLOAD_ROLE.key)
              }
            />
            <label
              className="form-check-label small"
              htmlFor="fileDownloadCheck"
            >
              Datei Download
            </label>
          </div>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="fileUploadCheck"
              checked={user.roles.includes(FILE_UPLOAD_ROLE.key)}
              onChange={(e) =>
                updateRole(e.target.checked, FILE_UPLOAD_ROLE.key)
              }
            />
            <label className="form-check-label small" htmlFor="fileUploadCheck">
              Datei Upload
            </label>
          </div>
        </div>
        <div className="col-6">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="addNumberCheck"
              checked={user.roles.includes(NUMER_ADD_ROLE.key)}
              onChange={(e) => updateRole(e.target.checked, NUMER_ADD_ROLE.key)}
            />
            <label className="form-check-label small" htmlFor="addNumberCheck">
              Nummer Hinzufügen
            </label>
          </div>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="verifyNumberCheck"
              checked={user.roles.includes(NUMBER_VERIFY_ROLE.key)}
              onChange={(e) =>
                updateRole(e.target.checked, NUMBER_VERIFY_ROLE.key)
              }
            />
            <label
              className="form-check-label small"
              htmlFor="verifyNumberCheck"
            >
              Nummer Verifizieren
            </label>
          </div>
        </div>
      </div>
      <div className="col-6">
        {props.mode === "EDIT" && (
          <>
            <div className="col-6">
              <span className="form-text">Account</span>
            </div>
            <button
              type="button"
              className="btn btn-dark btn-sm"
              disabled={!isPasswordResetEnabled}
              onClick={async () => {
                await apiFetch(`/api/user/password/reset/${user.userId}`, {
                  method: "POST",
                });

                toast?.showToast({
                  caption: "Password Reset",
                  message: `Eine E-Mail mit Instruktionen für den Passwort-Reset wurde an ${user.username}@${activeDomain} verschickt.`,
                });

                setIsPasswordResetEnabled(false);
              }}
            >
              Passwort Reset
            </button>
          </>
        )}
      </div>
    </>
  );
};

const AddUserModalContent = forwardRef(AddUserForm);

export type UserModalProps = {
  isAddUserModalOpen: boolean;
  setIsAddUserModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  domains: string[];
  selectedUser: User | undefined;
  setSelectedUser: (user: User | undefined) => void;
  userFormRef: RefObject<ForwardFormRef<User> | null>;
  handleAddUserSubmit: () => void;
};

export const UserModal: React.FC<UserModalProps> = ({
  isAddUserModalOpen,
  setIsAddUserModalOpen,
  domains,
  selectedUser,
  setSelectedUser,
  userFormRef,
  handleAddUserSubmit,
}: UserModalProps) => {
  return (
    <Modal
      isOpen={isAddUserModalOpen}
      onClose={() => setIsAddUserModalOpen(false)}
      title="Institut Benutzer"
      primaryAction={{
        label: "OK",
        onClick: (e) => {
          e.preventDefault();
          handleAddUserSubmit();
          setSelectedUser(undefined);
        },
      }}
      secondaryAction={{
        label: "Abbrechen",
        onClick: () => {
          setIsAddUserModalOpen(false);
          setSelectedUser(undefined);
        },
      }}
    >
      <AddUserModalContent
        ref={userFormRef}
        {...(selectedUser
          ? { mode: "EDIT", selectedUser, domains }
          : { mode: "INSERT", domains })}
      />
    </Modal>
  );
};
